import React, { useState } from 'react';
import moment from 'moment';

import { session } from '../../services/session';
import { api } from '../../services/api';

import BaseModal from '../base/modals/base-modal';
import dynamicInfo from '../../images/icons/dynamic-info.svg';
import pasteIcon from '../../images/icons/paste.svg';
import SearchModal from './search-modal';
import CreateStory from './create-story';
import CopyLink from './copy-link';

import './create-link.scss';

const dynamicModalText =
  'Dynamic SmartLinks are managed by Narrativ to maximize' +
  ' your earnings and avoid sending the user to out of stock products.';

function CreateLink() {
  /* STATE */
  const [product, setProduct] = useState('');
  const [story, setStory] = useState(null);
  const [link, setLink] = useState(null);
  const [dynamic, setDynamic] = useState(true);
  const [page, setPage] = useState('link');
  const [showInfoModal, setInfoModal] = useState(false);
  const [error, setError] = useState('');

  /* METHODS */
  function pasteFromClipboard() {
    navigator.clipboard.readText().then(text => {
      setProduct(text);
    });
  }

  function reset() {
    setProduct('');
    setStory(null);
    setLink(null);
    setDynamic(true);
    setError('');
    setPage('link');
  }

  function formatPublication(date = null) {
    if (!date) return;
    // Formatted date sent to backend, so we must translate using UTC
    return moment.utc(date).format('M/D/YYYY');
  }

  function createNewLink(event, data = null) {
    const url = `/api/v0/publishers/${session.orgId}/bam_links/`;
    const storyData = data || story;
    const additionalParams = !storyData.edit_id
      ? {
          edit_name: storyData.name,
          channel: storyData.channel,
          publication_date: storyData.publication_date.format('YYYY-MM-DD'),
          ...(storyData.edit_url ? { edit_url: storyData.edit_url } : {}),
        }
      : {
          edit_id: storyData.edit_id,
        };
    const params = {
      url: product,
      exclusive_match_requested: !dynamic,
      link_source: 6,
      ...additionalParams,
    };

    api
      .post(url, params)
      .then(response => {
        setError('');
        setLink(response.data.data[0]);
        setPage('copy');
      })
      .catch(e => {
        if (e && e.code === 4001) {
          e.message = 'Invalid Product URL';
        }
        if (page !== 'link') setPage('link');
        if (data && !story) setStory(data);
        setError((e && e.message) || 'Something went wrong, please try again');
      });
  }

  /* RENDER */
  const infoModal = showInfoModal ? (
    <BaseModal
      options={{
        displayTxt: dynamicModalText,
        cancel: {
          txt: 'Okay',
          callback: () => setInfoModal(false),
        },
      }}
    />
  ) : null;

  const searchModal =
    page === 'modal' ? <SearchModal setStory={setStory} setPage={setPage} /> : null;

  const createStory =
    page === 'story' ? <CreateStory setPage={setPage} createNewLink={createNewLink} /> : null;

  const copyLink = page === 'copy' ? <CopyLink link={link} reset={reset} /> : null;

  const flexButton = product ? (
    <div
      className="delete"
      onClick={() => {
        setProduct('');
        setError('');
        setStory(null);
      }}
    >
      x
    </div>
  ) : (
    <div className="paste" onClick={pasteFromClipboard}>
      <img src={pasteIcon} alt="paste button" />
    </div>
  );

  const storyName = story ? story.name : 'Search for a Story';
  const storyField = product ? (
    <div className="story-field" onClick={() => setPage('modal')}>
      <div className="title">Story/Video/Post (Required)</div>
      <div className="search-container">
        <div className="inner-container">
          <span className="narrativ-icon search" />
          <span className={`story-input ${story ? 'active' : ''}`}>{storyName}</span>
        </div>
        <span className="dropdown-icon"></span>
      </div>
    </div>
  ) : null;

  const descriptors = story ? (
    <div className="descriptors">
      <div className="channel">
        <span>Channel:</span>
        {story.channel}
      </div>
      <div className="date">
        <span>Publish Date:</span>
        {formatPublication(story.publication_date)}
      </div>
    </div>
  ) : null;

  const createButton =
    product && story ? (
      <div className="create-button" onClick={createNewLink}>
        Create Link
      </div>
    ) : null;

  const errorEl = error ? <div className="error">{error}</div> : null;

  const createLink =
    page === 'link' ? (
      <div className="outside-container">
        <div className="header">Create Narrativ Links</div>
        <div className="switch-row">
          <div className="info" onClick={() => setInfoModal(true)}>
            Dynamic Link
            <img className="info-icon" src={dynamicInfo} alt="info" />
          </div>
          <div className="switch-container" onClick={() => setDynamic(!dynamic)}>
            <div className={`switch ${dynamic ? 'dynamic' : ''}`}>
              <span></span>
            </div>
          </div>
        </div>
        <textarea
          className={`product-url ${product ? 'focus' : ''}`}
          placeholder="Paste product URL here..."
          onChange={e => setProduct(e.target.value)}
          value={product}
          spellCheck={false}
        />
        {flexButton}
        {storyField}
        {descriptors}
        {createButton}
        <div className="chrome-ext-text">
          To create links on desktop, use our
          <div>
            <a
              className="chrome-ext-link"
              href="https://chrome.google.com/webstore/detail/
					narrativ-for-publishers/nnpadnfeojdncgnecapnepeebnbeenip?hl=en-US"
              target="_blank"
              rel="noopener noreferrer"
            >
              Narrativ Chrome Extension.
            </a>
          </div>
        </div>
        {infoModal}
        {errorEl}
      </div>
    ) : null;

  return (
    <div className="create-link">
      {createLink}
      {searchModal}
      {createStory}
      {copyLink}
    </div>
  );
}

export default CreateLink;
